/**
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 */
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import HeadPage from "pages/StepPages/StepFour/HeadPage/HeadPage";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { pagesObj } from "../structure";
import Badge from "components-new/Badge";
import sass from "./page.module.sass";
import { Button, Search } from "components/shared";
import InputTemp from "components-new/InputTemp";
import FutureSelect from "components-new/FutureSelect";
import useDebounce from "hooks/useDebounce";
import { convertDate, isEmpty } from "helpers";
import { badge } from "pages/History/badge.setup";
import { useAppSelector } from "toolkit/hooks";
import Tooltip from "../../../components-new/Tooltip";
import { usePaginationWithIntersection } from "hooks/usePaginationWithIntersection";
import { userAPI } from "libs/apiClient/User";
import { usersAPI } from "libs/apiClient/Users";
import { OrganizationSimpleResponse, UserSimpleResponse } from "libs/apiClient/data-contracts";
import { organizationsAPI } from "libs/apiClient/Organizations";
import { auditAPI } from "libs/apiClient/Audit";
import { organizationAPI } from "libs/apiClient/Organization";

interface IRow {
	timestamp: string;
	responsible: { id: number; fullName: string };
	target?: { id: number; name?: string; fullName?: string; type?: "OKVED" | "SECTION" };
	action: string;
	user?: {
		fullName: string;
		id: number;
	};
	employee?: {
		fullName: string;
		id: number;
	};
	organization?: {
		id: 0;
		name: string;
	};
	role?: {
		id: number;
		name: string;
	};
	section?: string;
}

interface IInitUser {
	firstName: string;
	id: number | null;
	lastName: string;
	middleName: string;
}

const initialUser = {
	firstName: "",
	id: null,
	lastName: "Все действия",
	middleName: "",
};

interface IInitOrg extends Omit<OrganizationSimpleResponse, "id" | "archived"> {
	id: number | null;
}

const initialOrg: IInitOrg = {
	id: null,
	shortName: "Не выбрано",
	fullName: "",
};

const PageHistory = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const urlParams = useParams();

	const [page, setPage] = useState<{
		title: string;
		cols: string[];
		reference: { link: string; name: string };
		organization: boolean;
		useOrganization: boolean;
		category:
			| "ACTIVITY_AREA"
			| "BUSINESS_PROCESS_PATTERN"
			| "CONSTRUCTION_OBJECT"
			| "DOCUMENT"
			| "EMPLOYEE"
			| "INAPPLICABILITY_REASON"
			| "OBJECT_STATUS"
			| "OBJECT_TYPE"
			| "OKVED"
			| "ORGANIZATION"
			| "ORGANIZATION_DOCUMENT_PATTERN"
			| "ORGANIZATION_MARKER"
			| "PERSON_STATUS"
			| "OWNERSHIP_TYPE"
			| "ROLE"
			| "SIGNIFICANCE_CRITERIA"
			| "THREAT"
			| "THREAT_CONSEQUENCE"
			| "THREAT_MODEL_TEMPLATE"
			| "USER"
			| "CATEGORIZATION_STEP_ONE"
			| "CATEGORIZATION_STEP_TWO_ONE"
			| "CATEGORIZATION_STEP_TWO_TWO"
			| "CATEGORIZATION_STEP_THREE"
			| "CATEGORIZATION_STEP_FOUR_ONE"
			| "CATEGORIZATION_STEP_FOUR_TWO"
			| "CATEGORIZATION_STEP_FIVE"
			| "CATEGORIZATION_STEP_SIX"
			| "CATEGORIZATION_STEP_SEVEN";
	}>();

	const profile = useAppSelector((state) => state.profile.profile);

	const [rows, setRows] = useState<IRow[]>([]);

	const [currentOrg, setCurrentOrg] = useState<IInitOrg>(initialOrg);

	const [currentUser, setCurrentUser] = useState<IInitUser>(initialUser);

	const memoizeOrg = useRef<typeof currentOrg>({
		id: null,
		shortName: "Не выбрано",
		fullName: "",
	});
	const headRef = useRef<HTMLDivElement | null>(null);
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const [height, setHeight] = useState("0");

	const [organizations, setOrganizations] = useState<IInitOrg[]>([]);
	const [users, setUsers] = useState<UserSimpleResponse[]>([]);

	const [searchOrg, setSearchOrg] = useState("");
	const [searchUsers, setSearchUsers] = useState("");

	const [searchField, setSearchField] = useState("");

	const query = useDebounce(searchField, 400);

	const [params, setParams] = useState<{
		page: number;
		num: number;
		query: string;
		user: number | null;
		organization: number | null;
	}>({
		page: 1,
		num: 30,
		query: "",
		user: null,
		organization: null,
	});

	const firstLoad = useRef(true);

	const thenHandler = (data: IRow[]) => {
		if (params.page === 1) {
			if (data.length) {
				if (data.length < params.num) {
					setIsFinally(true);
				} else {
					setParams((prevState) => ({ ...prevState, page: prevState.page + 1 }));
				}
			} else {
				setIsFinally(true);
			}
			setRows(data);
		} else {
			if (data.length) {
				if (!(data.length < params.num)) {
					setParams((prevState) => ({ ...prevState, page: prevState.page + 1 }));
				} else {
					setIsFinally(true);
				}
				setRows((prevState) => [...prevState, ...data]);
			} else {
				setIsFinally(true);
			}
		}
		memoizeOrg.current = currentOrg;
	};
	const lastElement = useRef<HTMLDivElement | null>(null);

	const searchOrganization = useDebounce(searchOrg, 400);
	const searchUser = useDebounce(searchUsers, 400);

	const selectHandler = useCallback(
		(e: any, type: "org" | "user", properties: typeof params = params, request: boolean = true) => {
			let oldUrl = location.pathname;
			let par = {
				...params,
				...properties,
				...(properties.organization !== undefined && properties.organization === null && { organization: null }),
			};
			let newPath = oldUrl;
			if (type === "org") {
				setCurrentOrg(e);
				let temp = `/org/${e.id}`;
				if (/.+\/org\/(\d+).*/.test(newPath)) {
					if (e.id !== null) {
						newPath = newPath.replace(/\/org\/(\d+)/, temp);
					} else {
						newPath = newPath.replace(/(\/org\/\d+)/, "");
					}
				} else {
					newPath += temp;
				}
				setSearchOrg("");
				par = { ...par, organization: e.id, page: 1 };
			} else {
				setCurrentUser(e);
				let temp = `/user/${e.id}`;
				if (/.+\/user\/(\d+).*/.test(newPath)) {
					if (e.id !== null) {
						newPath = newPath.replace(/\/user\/(\d+)/, temp);
					} else {
						newPath = newPath.replace(/(\/user\/\d+)/, "");
					}
				} else {
					newPath += temp;
				}
				setSearchUsers("");
				par = { ...par, user: e.id, page: 1 };
			}
			setParams((params) => ({ ...params, ...par }));
			navigate(newPath, { state: location.state, replace: true });
			if (request) {
				setFetch(true);
				setIsFinally(false);
			}
			//eslint-disable-next-line
		},[params, urlParams],
	);

	const referenceLink = useMemo(() => {
		if (page) {
			if (page.organization) {
				return page.reference?.link.replace(/\/(\d+)\//, `/${memoizeOrg.current.id}/`);
			} else {
				return page.reference?.link;
			}
		}
		return "";
		//eslint-disable-next-line
	}, [page?.organization, page?.reference?.link, memoizeOrg.current]);

	const myProfile = useMemo(() => {
		let data: typeof currentUser = {} as typeof currentUser;
		if (!isEmpty(profile)) {
			data = {
				lastName: "Мои действия",
				firstName: "",
				middleName: "",
				id: profile.id || null,
			};
		}
		return data;
	}, [profile]);

	useEffect(() => {
		if (headRef.current) {
			setHeight(`calc(100vh - 164px - 47px - ${headRef.current?.getBoundingClientRect().height}px)`);
		}

		if (location.state && location.state.link) {
			const { link } = location.state;
			const current = pagesObj[link as keyof typeof pagesObj] || undefined;
			if (current !== undefined && typeof current === "object") {
				setPage(current as any);
			} else {
				navigate(-1);
			}
		}
		//eslint-disable-next-line
	}, [headRef.current]);

	// useEffect(() => {
	// 	setParams((prevState) => ({ ...prevState, organization: memoizeOrg.current.id }));
	// }, [memoizeOrg.current]);

	useEffect(() => {
		usersAPI
			.simpleList({
				page: 1,
				num: 10,
				query: searchUser,
			})
			.then((r) => setUsers(r.data));
	}, [searchUser]);

	useEffect(() => {
		if (page) {
			if (page?.organization || page?.useOrganization) {
				if (page.category === "ORGANIZATION_DOCUMENT_PATTERN") {
					organizationsAPI
						.documentPatternsList({
							page: 1,
							num: 50,
							query: searchOrganization,
						})
						.then((r) =>
							setOrganizations([
								{
									id: 0,
									shortName: "Системные",
									fullName: "Системные",
								},
								...r.data.map((_) => {
									const { archived, ...org } = _;
									return org;
								}),
							]),
						);
				} else {
					auditAPI
						.organizationsListList({
							page: 1,
							num: 50,
							query: searchOrganization,
							category: page?.category,
						})
						.then((r) => setOrganizations(r.data));
				}
			} else {
				organizationsAPI
					.allSimpleList({
						page: 1,
						num: 50,
						query: searchOrganization,
					})
					.then((r) => setOrganizations(r.data));
			}
		}
	}, [searchOrganization, page]);

	const firstRequest = useCallback(async () => {
		const requests = [];
		let arr: any[] = [];
		const newParams: typeof params = { ...params, page: 1 };
		if (urlParams.user) {
			// requests.push(
			await userAPI.profileList({ userid: Number(urlParams.user) }).then(({ data }) => {
				newParams.user = data.user?.id!;
				arr.push({
					firstName: data.user?.firstName,
					middleName: data.user?.middleName,
					lastName: data.user?.lastName,
					id: data.user?.id,
				});
			});
			// );
		}
		if (urlParams.org) {
			if (Number(urlParams.org) === 0) {
				// requests.push(
				// 	new Promise(() => {
				newParams.organization = null;
				memoizeOrg.current = {
					id: 0,
					fullName: "Системные",
					shortName: "Системные",
				};
				arr.push({
					id: 0,
					fullName: "Системные",
					shortName: "Системные",
				});
				// }),
				// );
			} else {
				// requests.push(
				await organizationAPI.organizationDetail(Number(urlParams.org)).then(({ data }) => {
					newParams.organization = data.id;
					memoizeOrg.current = {
						id: data.id,
						fullName: data.fullName,
						shortName: data.shortName,
					};
					arr.push({
						id: data.id,
						fullName: data.fullName,
						shortName: data.shortName,
					});
				});
				// );
			}
		}
		if (requests.length) {
			// Promise.all(requests).then(() => {
			// });
		}
		firstLoad.current = false;
		return { arr, newParams };
		//eslint-disable-next-line
	}, [firstLoad]);

	useEffect(() => {
		if (firstLoad.current) {
			firstRequest().then(({ newParams, arr }) => {
				arr.forEach((_: any, index) => {
					if (_?.shortName) {
						selectHandler(_, "org", newParams, index === arr.length - 1);
					} else {
						selectHandler(_, "user", newParams, index === arr.length - 1);
					}
				});
			});
		}
		//eslint-disable-next-line
	}, []);
	useEffect(() => {
		setParams((prevState) => ({ ...prevState, query, page: 1 }));
		setIsFinally(false);
		setFetch(true);
		//eslint-disable-next-line
	}, [query]);

	const { setIsFinally, setFetch } = usePaginationWithIntersection<IRow>({
		ref: scrollRef,
		params,
		dependencies: [!!page, page?.organization ? params.organization !== null : true],
		then: thenHandler,
		link: location.state.link,
		lastTarget: lastElement,
		catchHandler: () => {
			setParams((prevState) => ({ ...prevState, organization: memoizeOrg.current.id }));
			setCurrentOrg(memoizeOrg.current);
		},
	});
	const selectRef = useRef<HTMLDivElement | null>(null);
	return (
		<div className={`step-wrapper ${sass.wrapper}`}>
			<div ref={headRef}>
				<HeadPage
					subTitle={
						page?.useOrganization && (
							<FutureSelect
								ref={selectRef}
								options={[{ id: null, shortName: "Не выбрано" }, ...organizations]}
								query={searchOrg}
								setQuery={(e) => {
									setSearchOrg(e.target.value);
								}}
								current={currentOrg}
								field={"shortName"}
								value={currentOrg.shortName}
								placeholder={"Не выбрано"}
								onSelect={(e) => selectHandler(e, "org")}
							/>
						)
					}
					title={
						<div className={sass.headTitle}>
							{page?.title} <Badge color={"#7CADAA"}>История</Badge>
						</div>
					}>
					<div className={sass.headWrapper}>
						<InputTemp
							className={sass.searchUsers}
							currentValue={currentUser}
							placeholder={`${currentUser.lastName} ${currentUser.firstName} ${currentUser.middleName} `}
							value={searchUsers}
							field={["lastName", "firstName", "middleName"]}
							onChange={({ target }) => {
								setSearchUsers(target.value);
							}}
							options={[
								{
									firstName: "",
									id: null,
									lastName: "Все действия",
									middleName: "",
								},
								{ ...myProfile },
								...users,
							]}
							onSelect={(e) => selectHandler(e, "user")}
						/>

						<Tooltip
							color={"themeWhite"}
							menu={
								<div className={sass.helper}>
									Записи в истории действий очищаются по истечении 30 дней с момента их создания
								</div>
							}>
							<div className={sass.icon} />
						</Tooltip>

						<Search
							className={sass.search}
							value={searchField}
							onChange={(e) => setSearchField(e.target.value)}
							placeholder={"Поиск"}
						/>

						{page?.reference && (
							<Button
								disabled={page?.organization && memoizeOrg.current.id === null}
								onClick={() => {
									navigate(referenceLink, { state: location.state });
								}}>
								{page.reference.name}
							</Button>
						)}
					</div>
				</HeadPage>
			</div>
			<table>
				<thead>
					<tr>
						{page?.cols.map((_, index) => (
							<th key={index}>{_}</th>
						))}
					</tr>
				</thead>
			</table>
			{page?.organization ? (
				currentOrg.id !== null ? (
					<Table
						lastRef={lastElement}
						link={location.state.link}
						ref={scrollRef}
						references={referenceLink}
						rows={rows}
						maxHeight={height}
					/>
				) : (
					<div data-comment-false={true} style={{ height }} className={sass.empty}>
						Организация не выбрана
						<Button
							onMouseUp={(e) => {
								e.stopPropagation();
								e.preventDefault();
								selectRef.current?.click();
							}}>
							Выбрать организацию
						</Button>
					</div>
				)
			) : (
				<Table
					lastRef={lastElement}
					link={location.state.link}
					ref={scrollRef}
					references={referenceLink}
					rows={rows}
					maxHeight={height}
				/>
			)}
		</div>
	);
};

const Table: React.FC<any> = React.memo(
	forwardRef<HTMLDivElement | null, any>(({ lastRef, maxHeight, rows, link, references }, ref) => {
		const navigate = useNavigate();
		const navigationState = useCallback(
			(data: IRow) => {
				let payloadData: any = {};
				if (data.target && data.target.id) {
					if (data.target.type && (data.target.type === "OKVED" || data.target.type === "SECTION")) {
						// if (isPatternsNavigatePage) {
						// 	payloadData.outline = { doc:data.target,org: };
						// }else{
						payloadData.outline = data.target;
						// }
					} else {
						payloadData.outline = data.target.id;
					}
				}
				if (isEmpty(payloadData)) {
					return false;
				}
				return payloadData;
				//eslint-disable-next-line
			},[references],
		);

		const rowClick = useCallback(
			(e: React.MouseEvent<HTMLTableRowElement>, _: IRow) => {
				e.stopPropagation();
				const state = navigationState(_);
				let link: string;
				const isPatternsNavigatePage = /.+document-templates/.test(references);
				if (isPatternsNavigatePage) {
					link = references + "/" + _.organization?.id;
				} else {
					// eslint-disable-next-line react-hooks/exhaustive-deps
					link = /\/thread\/|\/calculate\/$/.test(references) ? `${(references += state.outline)}` : references;
				}
				state &&
					navigate(link, {
						state,
					});
			},
			[references],
		);

		return (
			<div data-comment-false={true} ref={ref} style={{ maxHeight }} className={sass.scroll}>
				<table>
					<tbody>
						{rows.map((_: IRow, index: number) => {
							const badgeSetup = (badge as any)[link][_.action];
							const empty =
								_.target === undefined &&
								_.employee === undefined &&
								_.user === undefined &&
								_.organization === undefined &&
								_.role === undefined &&
								_.section === undefined;
							const state = navigationState(_);
							return (
								<tr
									className={!isEmpty(state) ? sass.hovered : ""}
									key={index}
									onClick={(e) => {
										rowClick(e, _);
									}}>
									<td>{convertDate(_.timestamp, "D.M.Y hh:mm")}</td>
									{(_.target || _.target === null) && (
										<td>
											{!_.target ? (
												<span dangerouslySetInnerHTML={{ __html: "&mdash;" }} />
											) : _.role?.id && _.target.id ? (
												<Link onClick={(e) => e.stopPropagation()} to={`/user/${_.target.id}`}>
													{_.target.name || _.target.fullName}
												</Link>
											) : (
												_.target.name || _.target.fullName
											)}
										</td>
									)}
									{_.employee && (
										<td>
											{(
												<Link onClick={(e) => e.stopPropagation()} to={`/employee/${_.employee.id}`}>
													{_.employee.fullName}
												</Link>
											) || <span dangerouslySetInnerHTML={{ __html: "&mdash;" }} />}
										</td>
									)}
									{_.user && (
										<td>
											{(
												<Link onClick={(e) => e.stopPropagation()} to={`/user/${_.user.id}`}>
													{_.user.fullName}
												</Link>
											) || <span dangerouslySetInnerHTML={{ __html: "&mdash;" }} />}
										</td>
									)}
									{_.organization !== undefined && (
										<td>
											{_.organization !== null ? (
												<Link onClick={(e) => e.stopPropagation()} to={`/organization/${_.responsible.id}`}>
													{_.organization.name}
												</Link>
											) : (
												<span dangerouslySetInnerHTML={{ __html: "&mdash;" }} />
											)}
										</td>
									)}
									{_.role && (
										<td>
											{(
												<Link
													onClick={(e) => e.stopPropagation()}
													to={`/settings/roles`}
													state={{ outline: _.role.id }}>
													{_.role.name}
												</Link>
											) || <span dangerouslySetInnerHTML={{ __html: "&mdash;" }} />}
										</td>
									)}
									{_.section !== undefined && (
										<td>{_.section || <span dangerouslySetInnerHTML={{ __html: "&mdash;" }} />}</td>
									)}
									{empty && <td>&mdash;</td>}
									<td>
										<Badge color={badgeSetup.color}>{badgeSetup.text}</Badge>
									</td>
									<td>
										<Link onClick={(e) => e.stopPropagation()} to={`/user/${_.responsible.id}`}>
											{_.responsible.fullName}
										</Link>
									</td>
								</tr>
							);
						})}
						<tr ref={lastRef} />
					</tbody>
				</table>
			</div>
		);
	}),
);

export default PageHistory;
